export default {
  title: "Balance",
  balanceInfo: {
    title: "Balance Information",
    subtitle:
      "The RC balance displayed is the expected account amount after settlement of all transactions.",
    moreDetailSpoiler: {
      ru: [
        {
          title: "Actual Balance",
          description: [
            {
              type: "text",
              value:
                "Includes balance top-ups via bank, refunds for canceled transactions, credited points from customers. Minus point payouts according to reward distribution scenarios, deductions for tariff and service purchases, and the 'Reserved for Payment' amount (if set).",
            },
          ],
        },
        {
          title: "Pending Crediting",
          description: [
            {
              type: "text",
              value:
                "Points from customers that haven't been credited yet due to insufficient funds in your actual balance. Also includes customer journey rewards (from your digitized customers who visited other establishments) pending crediting. This occurs if the partner establishment visited by your digitized customer has insufficient funds.",
            },
          ],
        },
        {
          title: "Pending Debiting",
          description: [
            {
              type: "text",
              value:
                "Points that haven't been debited yet (according to reward distribution scenarios) due to insufficient funds, or if the establishment has a crediting delay set.",
            },
          ],
        },
        {
          title: "Available for Withdrawal",
          description: [
            {
              type: "text",
              value:
                "Actual balance minus funds pending debiting, the set deposit amount, and the 'Reserved for Payment' amount (if set).",
            },
          ],
        },
        {
          title: "Reserved for Payment",
          description: [
            {
              type: "text",
              value:
                "The amount that will actually be paid out during the settlement period.",
            },
            {
              type: "text",
              value:
                "Set twice a month in balance settings, minimum 300 RC and maximum the current 'Available for Withdrawal' balance. To set the payment amount, click 'Settings' in the legal entity's balance card for which you want to configure fund withdrawal.",
            },
            {
              type: "text",
              value:
                "If no amount is set before settlements and withdrawal based on the 'Available for Withdrawal' balance amount is not enabled, no funds will be withdrawn.",
            },
            {
              type: "text",
              value:
                "Estimated dates for fund deduction for payment from balance are from the 1st to 5th and from the 16th to 20th of each month. Payment to bank accounts is made within 14 days after the deduction date from the account balance.",
            },
          ],
        },
        {
          title: "Reconciliation Report",
          description: [
            {
              type: "v-text",
              value:
                "You can obtain reconciliation and work completion reports through the support service. To do this, submit a request via the feedback form in the bottom right corner of your RC CITY account or send an email to <a class='spoiler-link' href='mailto:hello@rc.city'>hello@rc.city</a>",
            },
            {
              type: "list",
              title: "The request must include:",
              list: [
                "Tax ID (INN)",
                "Legal entity name",
                "Period for which the reconciliation report is required, or amounts for which the work completion report is needed",
              ],
            },
          ],
        },
      ],
      noRu: [
        {
          title: "Actual Balance",
          description: [
            {
              type: "text",
              value:
                "Includes balance top-ups via bank, refunds for canceled transactions, credited points from customers. Minus point payouts according to reward distribution scenarios and deductions for tariff and service purchases.",
            },
          ],
        },
        {
          title: "Pending Crediting",
          description: [
            {
              type: "text",
              value:
                "Points from customers that haven't been credited yet due to insufficient funds in your actual balance for settlement. Also includes customer journey rewards (from your digitized customers who visited other establishments) pending crediting. This occurs if the partner establishment visited by your digitized customer has insufficient funds.",
            },
          ],
        },
        {
          title: "Pending Debiting",
          description: [
            {
              type: "text",
              value:
                "Points that haven't been debited yet (according to reward distribution scenarios) due to insufficient funds, or if the establishment has a crediting delay set.",
            },
          ],
        },
        {
          title: "Available for Withdrawal",
          description: [
            {
              type: "text",
              value:
                "Actual balance minus funds pending debiting and the set deposit amount.",
            },
          ],
        },
        {
          title: "Reconciliation Report",
          description: [
            {
              type: "v-text",
              value:
                "You can obtain reconciliation and work completion reports through the support service. To do this, submit a request via the feedback form in the bottom right corner of your RC CITY account or send an email to <a class='spoiler-link' href='mailto:hello@rc.city'>hello@rc.city</a>",
            },
            {
              type: "list",
              title: "The request must include:",
              list: [
                "Tax ID (INN)",
                "Legal entity name",
                "Period for which the reconciliation report is required, or amounts for which the work completion report is needed",
              ],
            },
          ],
        },
      ],
    },
  },
  withdrawalInstructionName:
    "Instructions for withdrawing funds from RC CITY account",
  reservedForPayment: "Reserved for Payment",
  withdrawalOfFund: "Withdrawal of Funds",
  power: {
    on: "Enabled",
    off: "Disabled",
  },
  actualBalance: "Actual Balance",
  expectedCredited: "Pending Crediting",
  expectedDebiting: "Pending Debiting",
  availableWithdrawal: "Available for Withdrawal",
  statusText: {
    needAdjustPaymentMethod:
      "To withdraw funds, you need to set up a payment method",
    bySumReservedOnBalance:
      "Based on the amount reserved in the balance until payment",
    bySumAvailableToWithdrawal:
      "Based on the 'Available for Withdrawal' amount at the time of settlement",
  },
  topupModal: {
    title: "Enter top-up amount",
    entityPayer: "Legal entity - payer",
    topupSum: "Top-up amount",
  },
  createAndDownloadAccount: "Generate and download invoice",
  byReservedSum: "By 'Reserved for Payment' amount",
  byAvailableSum: "By 'Available for Withdrawal' amount",
  bySettedSum: "By set amount",
  bySettedSumDesc:
    "Funds will be reserved until payment and cannot be used for purchasing tariffs and services, as the actual balance will be reduced by the set amount. This method is unavailable when the 'Available for Withdrawal' amount is less than 300 RC.",
  byAvailableSumDesc:
    "Funds will be paid out in an amount equal to the 'Available for Withdrawal' balance at the time of settlement, provided it is at least 300 RC.",
  success: "Success",
  settings: {
    title: "Withdrawal Settings",
    banner: {
      inProgress: {
        title: "Payments in progress",
        description: [
          "Changing withdrawal settings is unavailable during payment periods.",
          "You can set up settings for the next payment after the current fund withdrawal process is completed.",
        ],
      },
      inPending: {
        title: "Fund payment pending",
        description: [
          "It will be carried out according to contract clause 4.6. Estimated dates for fund deduction for payment from balance are from the 1st to 5th and from the 16th to 20th of each month. Payment to bank accounts is made within 14 days after the deduction date from the account balance.",
          "To cancel the payment, change the payment method, or set a different amount, click the 'Disable Fund Withdrawal' button.",
        ],
      },
      inDisabled: {
        title: "Fund withdrawal disabled",
        description: [
          "To withdraw funds, select a payment method and apply the settings. Payment will be made according to contract clause 4.6.",
          "Estimated dates for fund deduction for payment from balance are from the 1st to 5th and from the 16th to 20th of each month. Payment to bank accounts is made within 14 days after the deduction date from the account balance.",
          "If nothing is selected, no funds will be paid out for this legal entity during the specified period.",
        ],
      },
    },
    paymentMethod: "Payment Method",
    sumToBePaid: "Amount to be Paid",
    validSumTobePaidValid:
      "You can set an amount no less than 300 RC and no more than the current 'Available for Withdrawal' balance",
    entity: "Legal Entity",
    notSelectedNoPayment: "Not selected, no payment will be made",
    bank: {
      BIC: "Bank BIC",
      naming: "Bank Name",
      paymentAccount: "Checking Account Number",
      corpAccount: "Correspondent Account",
      depositAmount: "Deposit Amount",
      depositAmountHint:
        "Minimum account balance. The amount that remains in the balance when withdrawing funds.",
      changeDetails: "Change details",
    },
    disableWithdrawal: "Disable Fund Withdrawal",
    disabledWithdrawalForDefinEntity:
      "Fund withdrawal for {entityName} legal entity is disabled",
  },
  editPaymentModal: {
    title: "Want to change bank details or deposit amount?",
    subtitle:
      "For editing, you will be redirected to the 'Legal Entities' section. After making changes, return to the 'Balance' page to set up fund withdrawal with the new details.",
    toEdit: "Go to Edit",
  },
  resetPaymentModal: {
    title: "Cancel withdrawal settings?",
    subTitle:
      "When clicking 'Yes, Cancel', the withdrawal settings will be reset. No funds will be paid out for this legal entity.",
    yesCancel: "Yes, Cancel",
  },
  disabledPaymentModal: {
    title: "Confirm Disabling Fund Withdrawal",
    subtitle: {
      withFullWithdrawal: [
        "By clicking 'Confirm', the previously set payment amount will be released from reserve. After this, you will be able to change the payment method or set a new payment amount.",
        "If a new withdrawal method is not selected, no payment will be made for this legal entity.",
        "Important! Setting a new payment amount will only be possible within the 'Available for Withdrawal' balance, which may differ from the current one after canceling the reservation.",
      ],
      withoutFullWithdrawal: [
        "After disabling, you can enable it again or select a different payment method.",
        "If a new withdrawal method is not selected, no payment will be made for this legal entity.",
      ],
    },
  },
};
